import { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { FloatingPortal, useFloating, autoUpdate, offset } from '@floating-ui/react';
import I18n from 'I18n';
import FeedbackSurveyPrompt from 'growth-onboarding-feedback-components/components/FeedbackSurveyPrompt';
import { trackUserInteraction } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FLOATING_FEEDBACK_CONTAINER_MIN_WIDTH = 300;
const FloatingFeedbackContainer = styled.div.withConfig({
  displayName: "FloatingFeedback__FloatingFeedbackContainer",
  componentId: "vrcqsk-0"
})(({
  theme
}) => css(["background-color:", ";padding:", " ", ";border-radius:", ";border:", " solid ", ";box-shadow:", ";opacity:0;transition:opacity 0.2s ease-out;min-width:", "px;"], theme.color['fill-surface-default'], theme.space['200'], theme.space['300'], theme.borderRadius['100'], theme.borderWidth['100'], theme.color['border-core-default'], theme.boxShadow['100'], FLOATING_FEEDBACK_CONTAINER_MIN_WIDTH));
const getPositiveSelectableResponses = () => [I18n.text('coachingTips.feedbackPrompt.positiveResponses.easyToUnderstand'), I18n.text('coachingTips.feedbackPrompt.positiveResponses.helpfulInformation'), I18n.text('coachingTips.feedbackPrompt.positiveResponses.actionable'), I18n.text('coachingTips.feedbackPrompt.positiveResponses.quickToFind'), I18n.text('coachingTips.feedbackPrompt.positiveResponses.clearAndConcise')];
const getNegativeSelectableResponses = () => [I18n.text('coachingTips.feedbackPrompt.negativeResponses.hardToUnderstand'), I18n.text('coachingTips.feedbackPrompt.negativeResponses.unhelpfulInformation'), I18n.text('coachingTips.feedbackPrompt.negativeResponses.tooMuchInformation'), I18n.text('coachingTips.feedbackPrompt.negativeResponses.gotInTheWay'), I18n.text('coachingTips.feedbackPrompt.negativeResponses.notRelevant')];
export const FloatingFeedback = ({
  zIndex,
  coachingTipsId,
  feedbackSurveyId,
  tipId
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const {
    refs,
    floatingStyles,
    isPositioned
  } = useFloating({
    placement: 'bottom',
    middleware: [offset(20)],
    whileElementsMounted: autoUpdate
  });
  const handleOpenComplete = useCallback(() => {
    trackUserInteraction('open feedback modal', coachingTipsId, tipId);
  }, [coachingTipsId, tipId]);
  const handlePromptOptionClick = useCallback(promptOption => {
    trackUserInteraction(`click ${promptOption} feedback`, coachingTipsId, tipId);
    setShowPrompt(false);
  }, [coachingTipsId, tipId]);
  const handlePromptReady = useCallback(() => {
    trackUserInteraction('view feedback', coachingTipsId, tipId);
    setShowPrompt(true);
  }, [coachingTipsId, tipId]);
  const handleSubmitComplete = useCallback(() => {
    trackUserInteraction('submit feedback', coachingTipsId, tipId);
  }, [coachingTipsId, tipId]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      ref: refs.setReference,
      "data-test-id": "floating-feedback-reference"
    }), /*#__PURE__*/_jsx(FloatingPortal, {
      root: document.body,
      children: /*#__PURE__*/_jsx(FloatingFeedbackContainer, {
        ref: refs.setFloating,
        style: Object.assign({}, floatingStyles, {
          zIndex,
          opacity: showPrompt && isPositioned ? 1 : 0
        }),
        "data-test-id": "floating-feedback-container",
        children: /*#__PURE__*/_jsx(FeedbackSurveyPrompt, {
          surveyId: feedbackSurveyId,
          surveyType: "CSAT",
          positiveButtonText: I18n.text('coachingTips.feedbackPrompt.positiveButton'),
          negativeButtonText: I18n.text('coachingTips.feedbackPrompt.negativeButton'),
          promptText: I18n.text('coachingTips.feedbackPrompt.promptText'),
          positiveTitle: I18n.text('coachingTips.feedbackPrompt.positiveTitle'),
          negativeTitle: I18n.text('coachingTips.feedbackPrompt.negativeTitle'),
          buttonText: I18n.text('coachingTips.feedbackPrompt.submitButton'),
          placeholder: I18n.text('coachingTips.feedbackPrompt.placeholder'),
          subTitle: I18n.text('coachingTips.feedbackPrompt.subTitle'),
          positiveSelectableResponses: getPositiveSelectableResponses(),
          negativeSelectableResponses: getNegativeSelectableResponses(),
          onOpenComplete: handleOpenComplete,
          onPromptOptionClick: handlePromptOptionClick,
          onReady: handlePromptReady,
          onSubmitComplete: handleSubmitComplete
        })
      })
    })]
  });
};